import { authenticatedRequest } from "../authenticate/authenticatedRequest";

export type SitePipelineStatus = {
  siteId: string;
  queueId?: string;
  queueName?: string;
  status: string;
  currentOp?: number;
  operations?: string[];
  lastOpResult?: string;
  source?: string;
};

export type PipelineKind = "provision" | "clone";

export type PipelineOutcome =
  | { kind: "success" }
  | { kind: "failure"; rolledBack: boolean; detail: string }
  | { kind: "in_progress" };

const TERMINAL = new Set(["COMPLETED", "ERROR", "OP_NOT_FOUND"]);

export function normalizePipelineStatus(status?: string | null): string {
  return (status || "").trim().toUpperCase();
}

export function isTerminalPipelineStatus(status?: string | null): boolean {
  return TERMINAL.has(normalizePipelineStatus(status));
}

export function isPipelineSuccess(status?: string | null): boolean {
  return normalizePipelineStatus(status) === "COMPLETED";
}

/**
 * Interprets a polled status into a UI outcome.
 * Success is ONLY status COMPLETED — never inferred from lastOpResult alone
 * (ASYNC_COMPLETE is an intermediate handoff while status may still be AWAITING).
 */
export function interpretPipelineStatus(
  status: SitePipelineStatus,
  opts?: { siteStillExists?: boolean }
): PipelineOutcome {
  const normalized = normalizePipelineStatus(status.status);
  if (!TERMINAL.has(normalized)) {
    return { kind: "in_progress" };
  }
  if (normalized === "COMPLETED") {
    return { kind: "success" };
  }

  const op =
    status.operations && status.currentOp != null
      ? status.operations[status.currentOp]
      : undefined;
  const parts = [`pipeline status ${normalized}`];
  if (op) {
    parts.push(`step ${op}`);
  }
  if (status.lastOpResult) {
    parts.push(`lastOpResult ${status.lastOpResult}`);
  }
  const detail = parts.join(", ");

  // Do not assume rollback — confirm via site existence when provided.
  const rolledBack =
    opts?.siteStillExists === undefined ? false : !opts.siteStillExists;

  return { kind: "failure", rolledBack, detail };
}

export function pipelineOutcomeToast(
  kind: PipelineKind,
  siteId: string,
  outcome: Extract<PipelineOutcome, { kind: "success" | "failure" }>
): { message: string; severity: "success" | "error" | "warning" } {
  if (outcome.kind === "success") {
    return {
      severity: "success",
      message:
        kind === "clone"
          ? `Clone of site ${siteId} completed successfully.`
          : `Provision of site ${siteId} completed successfully.`,
    };
  }

  if (outcome.rolledBack) {
    return {
      severity: "error",
      message:
        kind === "clone"
          ? `Clone failed for site ${siteId} (${outcome.detail}). Created site resources were rolled back so you can retry with the same ID.`
          : `Provision failed for site ${siteId} (${outcome.detail}). Created site resources were rolled back so you can retry with the same ID.`,
    };
  }

  return {
    severity: "warning",
    message:
      kind === "clone"
        ? `Clone pipeline reported failure for site ${siteId} (${outcome.detail}), but the site record still exists. Earlier steps may have succeeded — check clone-queue / Cloudflare logs for this step before retrying.`
        : `Provision pipeline reported failure for site ${siteId} (${outcome.detail}), but the site record still exists. Check pipeline history before retrying or destroying the site.`,
  };
}

export function parseQueueId(payload: unknown): string {
  if (typeof payload === "string") {
    return payload;
  }
  if (payload && typeof payload === "object" && "queueId" in payload) {
    const q = (payload as { queueId?: unknown }).queueId;
    if (typeof q === "string" || typeof q === "number") {
      return String(q);
    }
  }
  // JSON-encoded string response from Go *string marshal
  if (payload != null) {
    return String(payload).replace(/^"|"$/g, "");
  }
  return "";
}

export async function fetchSitePipelineStatus(
  siteId: string,
  qid?: string
): Promise<SitePipelineStatus> {
  const qs = qid ? `?qid=${encodeURIComponent(qid)}` : "";
  const res = await authenticatedRequest(
    `${process.env.REACT_APP_PROXY_SERVER}/api/sites/${siteId}/status${qs}`
  );
  if (!res.ok) {
    throw new Error(`Failed to fetch pipeline status (${res.status})`);
  }
  return res.json();
}

export async function siteRecordExists(siteId: string): Promise<boolean> {
  const res = await authenticatedRequest(
    `${process.env.REACT_APP_PROXY_SERVER}/api/sites/${siteId}`
  );
  return res.ok;
}

export type WatchPipelineOptions = {
  intervalMs?: number;
  maxAttempts?: number;
  onUpdate?: (status: SitePipelineStatus) => void;
  signal?: AbortSignal;
};

/**
 * Polls site pipeline status until COMPLETED/ERROR or max attempts.
 * Ignores non-terminal statuses including IDLE/PROCESSING/AWAITING and ASYNC_* lastOpResult.
 */
export async function watchSitePipeline(
  siteId: string,
  qid: string,
  options: WatchPipelineOptions = {}
): Promise<SitePipelineStatus> {
  const intervalMs = options.intervalMs ?? 5000;
  const maxAttempts = options.maxAttempts ?? 360; // ~30 minutes at 5s

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (options.signal?.aborted) {
      throw new DOMException("Aborted", "AbortError");
    }
    const status = await fetchSitePipelineStatus(siteId, qid);
    options.onUpdate?.(status);
    if (isTerminalPipelineStatus(status.status)) {
      return status;
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }

  throw new Error(
    `Timed out waiting for pipeline ${qid} on site ${siteId} to finish`
  );
}

export function pipelineInProgressLabel(
  kind: PipelineKind,
  status?: SitePipelineStatus
): string {
  const op =
    status?.operations && status.currentOp != null
      ? status.operations[status.currentOp]
      : undefined;
  const base =
    kind === "clone"
      ? "Clone in progress — importing database and media. This usually takes longer than a standard provision."
      : "Provision in progress — installing WordPress and configuring the site.";
  return op
    ? `${base} Current step: ${op} (${status?.status}).`
    : `${base} Status: ${status?.status || "starting"}…`;
}
